import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '../../GlobalStyles';

interface LetterTextModalProps {
    isOpen: boolean;
    onClose: () => void;
    letterText: string;
    commentText: string;
    letterCommentNum: number;
}

const LetterTextModal: React.FC<LetterTextModalProps> = ({
    isOpen,
    onClose,
    letterText,
    commentText,
    letterCommentNum,
}) => {
    const highlightRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (isOpen && highlightRef.current) {
            // Auto-scroll to the highlighted text with a slight delay
            setTimeout(() => {
                highlightRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 100);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Function to render text with highlighted portion
    const renderHighlightedText = () => {
        if (!letterText || !commentText) {
            return <p>{letterText || 'No letter text available'}</p>;
        }

        const index = letterText.toLowerCase().indexOf(commentText.toLowerCase());
        
        if (index === -1) {
            // If comment text not found in letter text, just show the letter text
            return <p>{letterText}</p>;
        }

        const beforeHighlight = letterText.substring(0, index);
        const highlighted = letterText.substring(index, index + commentText.length);
        const afterHighlight = letterText.substring(index + commentText.length);

        return (
            <p>
                {beforeHighlight}
                <Highlight ref={highlightRef}>{highlighted}</Highlight>
                {afterHighlight}
            </p>
        );
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>Letter Comment #{letterCommentNum}</ModalTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>
                <ModalBody>
                    {renderHighlightedText()}
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContainer>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 24px;
    color: #333;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 32px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div`
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    
    p {
        font-size: 16px;
        line-height: 1.6;
        color: #333;
        white-space: pre-wrap;
        word-wrap: break-word;
    }
`;

const Highlight = styled.span`
    background-color: #ffeb3b;
    padding: 2px 4px;
    border-radius: 3px;
    font-weight: 600;
    color: #000;
`;

const ModalFooter = styled.div`
    padding: 20px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

export default LetterTextModal;
